import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/Card";
import { Button } from "@/components/Button";
import { Alert } from "@/components/Alert";
import {
  MessageCategoryPicker,
  CategoryBadge,
  type SelectableCategory,
} from "@/components/MessageCategoryPicker";
import {
  useMessages,
  useSendMessage,
  useUnreadMessageCount,
} from "@/hooks/useMessages";
import {
  searchArticles,
  getArticlesByCategory,
  type HelpArticle,
  type HelpCategoryId,
} from "@/data/helpArticles";

const MAX_DASHBOARD_BODY_LEN = 1000; // shorter than Messages page's 5000 — encourages full conversations there
const SUCCESS_FLASH_MS = 4000;
const MAX_SUGGESTIONS = 3;
const MIN_SEARCH_LEN = 3; // chars typed before live search kicks in

// Map message category → help-center category for default suggestions
// (shown before the patient has typed anything substantive). Once the
// patient types 3+ chars, we switch to live keyword search across ALL
// help articles, since their question might cross categories.
const DEFAULT_HELP_CATEGORY: Record<SelectableCategory, HelpCategoryId> = {
  medical: "treatments",
  support: "service",
  billing: "billing",
};

/**
 * Compact "message your care team" widget for the Dashboard.
 *
 * - Surfaces unread inbound count with a soft prompt
 * - Shows last inbound message preview (single line, truncated) so the
 *   patient sees pending care-team replies without leaving Dashboard
 * - 3-row textarea + send button for quick outbound messages
 * - On send, ephemeral success state shows for 4 seconds, then resets
 * - "View full conversation →" link routes to /messages for the full thread
 *
 * Reuses the existing useSendMessage/useMessages/useUnreadMessageCount
 * hooks — does NOT introduce a new edge function or RPC.
 */
export function QuickMessageCard({ patientId }: { patientId: string | undefined }) {
  const messagesQ = useMessages(patientId);
  const unreadQ = useUnreadMessageCount(patientId);
  const send = useSendMessage(patientId);

  const [draft, setDraft] = useState("");
  const [category, setCategory] = useState<SelectableCategory | null>(null);
  // Manually dismiss the suggestions card if the patient really wants to
  // send rather than self-serve. Resets when category changes so a new
  // category gets a fresh suggestion attempt.
  const [suggestionsDismissed, setSuggestionsDismissed] = useState(false);
  useEffect(() => { setSuggestionsDismissed(false); }, [category]);

  // Suggested help articles. Behavior:
  //   - No category picked → no suggestions (we don't know what to suggest)
  //   - Category picked, <3 chars typed → top N from that category's help bucket
  //   - 3+ chars typed → live keyword search across ALL articles (cross-category)
  // searchArticles is O(N) over ~31 articles — trivially fast, no debounce needed.
  const suggestions: HelpArticle[] = useMemo(() => {
    if (!category) return [];
    const q = draft.trim();
    if (q.length >= MIN_SEARCH_LEN) {
      return searchArticles(q, MAX_SUGGESTIONS);
    }
    return getArticlesByCategory(DEFAULT_HELP_CATEGORY[category]).slice(0, MAX_SUGGESTIONS);
  }, [draft, category]);
  // Remember which category was sent so the success line can name the
  // correct destination team (care / support / billing). We used to
  // interpolate the edge function's `routed_to` label directly, but
  // those strings ("Medical question", etc.) read like a bug — these
  // hand-picked labels match how patients actually think about teams.
  const [justSent, setJustSent] = useState<{ category: SelectableCategory } | null>(null);

  // Auto-clear the "just sent" success state after a few seconds so the
  // next message attempt starts clean.
  useEffect(() => {
    if (!justSent) return;
    const t = setTimeout(() => setJustSent(null), SUCCESS_FLASH_MS);
    return () => clearTimeout(t);
  }, [justSent]);

  const charsLeft = MAX_DASHBOARD_BODY_LEN - draft.length;
  const canSend = draft.trim().length > 0 && category != null && !send.isPending;

  // Find the most recent INBOUND (care team → patient) message for the
  // preview line. Patient's own messages don't count as "pending replies."
  const messages = messagesQ.data ?? [];
  const lastInbound = [...messages]
    .reverse()
    .find((m) => m.direction === "inbound");
  const unread = unreadQ.data ?? 0;

  async function handleSend() {
    const body = draft.trim();
    if (!body || !category || send.isPending) return;
    try {
      const sentCategory = category;  // capture before reset
      await send.mutateAsync({ body, category });
      setDraft("");
      setCategory(null);
      setJustSent({ category: sentCategory });
    } catch {
      // Error surfaces in the Alert below via send.error
    }
  }

  // Cmd/Ctrl + Enter to send — small power-user nicety
  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      if (canSend) handleSend();
    }
  }

  return (
    <Card
      title="Message your care team"
      action={
        unread > 0 ? (
          <Link to="/messages" className="text-fh-green hover:text-fh-green-dark">
            {unread} unread →
          </Link>
        ) : (
          <Link to="/messages" className="text-fh-green hover:text-fh-green-dark">
            View all →
          </Link>
        )
      }
    >
      {/* Recent reply preview — shows the most recent care-team message
          so patients see pending context without leaving the dashboard */}
      {lastInbound && (
        <div className="mb-3 p-3 rounded-[10px] bg-fh-sage/40 border border-fh-green-light/40">
          <div className="flex items-center justify-between mb-1.5 gap-2 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[11px] uppercase tracking-wider font-semibold text-fh-green-dark">
                From {lastInbound.sender_display_name || "Care team"}
              </span>
              {lastInbound.category !== "general" && (
                <CategoryBadge category={lastInbound.category} />
              )}
            </div>
            <span className="text-[10.5px] text-fh-gray">
              {formatRelative(lastInbound.created_at)}
            </span>
          </div>
          <p className="text-[13.5px] text-fh-ink m-0 line-clamp-2 leading-snug">
            {lastInbound.body}
          </p>
        </div>
      )}

      {/* Send error if any */}
      {send.error && (
        <div className="mb-3">
          <Alert tone="error">{(send.error as Error).message}</Alert>
        </div>
      )}

      {/* Just-sent confirmation */}
      {justSent && (
        <div className="mb-3">
          <Alert tone="success">
            Sent to {teamLabelFor(justSent.category)} — typically responds within 1 business day.
          </Alert>
        </div>
      )}

      {/* Category selector — REQUIRED before send. Drives routing to
          clinician inbox (medical) vs care team (support/billing). */}
      <div className="mb-3">
        <MessageCategoryPicker value={category} onChange={setCategory} variant="compact" />
      </div>

      {/* FAQ deflection — when the patient picks a category (and before
          they send), surface up to 3 Help Center articles that match
          their question. Many patients self-serve here and the care
          team only sees the messages that truly need a human reply.
          Patient can dismiss the panel to hide it for this category. */}
      {category && !justSent && !suggestionsDismissed && suggestions.length > 0 && (
        <div className="mb-3 p-3 rounded-[10px] bg-fh-sand/40 border border-fh-border">
          <div className="flex items-start justify-between gap-2 mb-2">
            <span className="text-[11px] uppercase tracking-wider font-semibold text-fh-gray">
              {draft.trim().length >= MIN_SEARCH_LEN
                ? "Articles that match what you're asking"
                : "Common questions in this category"}
            </span>
            <button
              type="button"
              onClick={() => setSuggestionsDismissed(true)}
              className="text-[11px] text-fh-gray-light hover:text-fh-gray underline-offset-2 hover:underline shrink-0"
              aria-label="Hide suggestions"
            >
              hide
            </button>
          </div>
          <ul className="space-y-2 m-0 p-0 list-none">
            {suggestions.map((a) => (
              <li key={a.id}>
                <Link
                  to={`/help/${a.id}`}
                  className="block p-2 -mx-2 rounded-[8px] hover:bg-white transition group"
                >
                  <div className="text-[13px] font-semibold text-fh-green-dark group-hover:text-fh-green leading-snug">
                    {a.title} →
                  </div>
                  <div className="text-[11.5px] text-fh-gray leading-snug mt-0.5 line-clamp-2">
                    {a.summary}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
          <Link
            to="/help"
            className="text-[11.5px] text-fh-green-dark hover:underline mt-1 inline-block"
          >
            Browse all FAQs →
          </Link>
        </div>
      )}

      {/* Textarea + send */}
      <div>
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value.slice(0, MAX_DASHBOARD_BODY_LEN))}
          onKeyDown={handleKeyDown}
          rows={3}
          maxLength={MAX_DASHBOARD_BODY_LEN}
          placeholder={
            category === "medical"
              ? "Describe your symptoms or question for the clinician…"
              : category === "billing"
              ? "Describe your billing question or issue…"
              : category === "support"
              ? "What can we help you with?"
              : "Pick a category above, then type your message…"
          }
          className="w-full text-[14px] text-fh-ink bg-white border border-fh-border rounded-[10px] p-3 resize-none focus:outline-none focus:border-fh-green focus:ring-2 focus:ring-fh-green/20 placeholder:text-fh-gray-light disabled:bg-fh-sand/40 disabled:cursor-not-allowed"
          aria-label="Message to care team"
          disabled={category == null}
        />

        <div className="flex items-center justify-between mt-2.5 gap-3">
          <span className="text-[11px] text-fh-gray-light">
            {!category
              ? "Pick a category above to start typing"
              : draft.length > 0
              ? `${charsLeft} characters left`
              : "Replies within 1 business day. Not for emergencies."}
          </span>
          <Button
            size="sm"
            disabled={!canSend}
            loading={send.isPending}
            onClick={handleSend}
          >
            Send
          </Button>
        </div>
      </div>

      {/* Emergency disclaimer — small, persistent, required for healthcare UX */}
      <p className="text-[10.5px] text-fh-gray mt-3 leading-snug m-0">
        For medical emergencies, call <strong>911</strong>. For urgent clinical concerns outside business hours, contact your primary care provider or visit an urgent care.
      </p>
    </Card>
  );
}

// Patient-facing label for which team a message routes to, keyed by the
// category the patient picked. Intentionally separate from the routing
// labels the edge function returns in `routed_to` (those say "Medical
// question" / "Support question" etc., which read like a bug).
function teamLabelFor(c: SelectableCategory): string {
  switch (c) {
    case "medical": return "care team";
    case "support": return "support team";
    case "billing": return "billing team";
  }
}

function formatRelative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return "just now";
  const m = Math.round(ms / 60_000);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
